/**
 * EmailField - Email input field component
 *
 * Task: T-P4-D2-F4b-4
 * Feature: P4-D2-F4 Web App Forms UI
 *
 * Email input with validation icon showing success/error state.
 * Icon appears after user starts typing.
 */

import { useState, type ChangeEvent } from 'react';
import type { TextFieldProps } from './types';
import { FieldWrapper } from './FieldWrapper';
import { SuccessIcon, XIcon } from './Icons';
import styles from './fields.module.css';

// Email validation regex (matches backend)
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * EmailField renders an email input with real-time validation icon.
 */
export function EmailField({
  field,
  value,
  onChange,
  onBlur,
  error,
  style,
  nonInteractive,
}: TextFieldProps) {
  const [touched, setTouched] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const handleBlur = () => {
    setTouched(true);
    onBlur();
  };

  // Determine validation state for icon
  const isValid = value && EMAIL_REGEX.test(value);
  const isInvalid = touched && value && !isValid;
  const showIcon = value.length > 0;

  // Determine input classes
  const inputClasses = [
    styles.input,
    error ? styles.hasError : '',
    isValid && touched ? styles.hasSuccess : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <FieldWrapper
      fieldId={field.fieldId}
      label={field.label}
      required={field.required}
      helpText={field.helpText}
      error={error}
      style={style}
    >
      <div className={styles.inputWithIcon}>
        <input
          type="text"
          inputMode="email"
          id={field.fieldId}
          name={field.fieldId}
          className={inputClasses}
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={field.placeholder}
          maxLength={field.maxLength}
          readOnly={nonInteractive}
          aria-invalid={!!error}
          aria-describedby={error ? `${field.fieldId}-error` : undefined}
          autoComplete="email"
        />
        {/* Success icon */}
        <SuccessIcon
          className={`${styles.inputIcon} ${styles.success} ${
            showIcon && isValid ? styles.visible : ''
          }`}
          aria-hidden="true"
        />
        {/* Error icon */}
        <XIcon
          className={`${styles.inputIcon} ${styles.error} ${
            showIcon && isInvalid ? styles.visible : ''
          }`}
          aria-hidden="true"
        />
      </div>
    </FieldWrapper>
  );
}
